import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import { LoggerMolecule } from "@/instrumentation/logger/logger-molecule"
import { Flow } from "@/instrumentation/logger/types"
import type { NftMiniature } from "@anaconda/core/nft/types"
import { Trans } from "@lingui/react/macro"
import {
  type Blockchain,
  type Order,
  acceptBidFactoryReatom,
} from "@rarible_int/anaconda_sdk/ethereum"
import { sonnerToast } from "@rarible_int/anaconda_ui_components"
import { action, withAsync, withCallHook, wrap } from "@reatom/core"
import { molecule } from "bunshi"
import { EvmSdkContainerMoleculeReatom } from "../../blockchains/evm-sdk-container-molecule.reatom"
import { AppToast, SuccessViewTransaction } from "../../toasts/app-toast"
import {
  trackTransactionCompleted,
  trackTransactionFailed,
  trackTransactionStarted,
} from "../events/track-transaction"
import { getTransactionErrorMessage } from "../events/transaction-event"
import { invalidateBoughtNftQueries } from "../order/invalidate-bought-nft-queries"
import { invalidateNftOffersQueries } from "../order/invalidate-nft-offers-queries"

export interface EvmAcceptBidActionParams {
  nft: NftMiniature
  order: Order
  blockchain: Blockchain
}

export const EvmAcceptBidTransactionMoleculeReatom = molecule((mol) => {
  const logger = mol(LoggerMolecule)
  const { sdkContainerAtom } = mol(EvmSdkContainerMoleculeReatom)
  const { evmAddress: evmAddressAtom } = mol(WalletConnectMoleculeReatom)

  const acceptBidActionFactory = () => {
    const acceptBidAction = action(
      async ({ nft, order, blockchain }: EvmAcceptBidActionParams) => {
        const from = evmAddressAtom()
        const container = sdkContainerAtom()

        if (!from) {
          throw new Error("Wallet address is not available to accept an offer")
        }

        const acceptBid = acceptBidFactoryReatom(container)
        const { txId } = await wrap(
          acceptBid({ from, blockchain, itemId: nft.id, order }),
        )

        return { txId, nft, blockchain }
      },
      "evmAcceptBidAction",
    ).extend(
      withAsync(),
      withCallHook((_, [{ nft }]) => {
        sonnerToast.custom(
          (id) => (
            <AppToast.Pending
              id={id}
              title="Accept offer"
              main={<Trans>Accept offer</Trans>}
              second={<Trans>Pending...</Trans>}
            />
          ),
          { duration: Number.POSITIVE_INFINITY },
        )
        trackTransactionStarted({ logger, flow: Flow.AcceptOffer, nft })
      }),
    )

    acceptBidAction.onReject.extend(
      withCallHook(({ error, params: [{ nft }] }) => {
        console.error("Accept offer failed:", error)
        trackTransactionFailed({ logger, flow: Flow.AcceptOffer, nft, error })

        const message = getTransactionErrorMessage(error)
        sonnerToast.dismiss()
        sonnerToast.custom((id) => (
          <AppToast.Error
            id={id}
            title="Accept offer"
            main={<Trans>Accepting the offer failed</Trans>}
            second={
              <div className="font-semibold text-destructive">{message}</div>
            }
          />
        ))
      }),
    )

    acceptBidAction.onFulfill.extend(
      withCallHook(({ payload }) => {
        const { txId, nft, blockchain } = payload
        // The NFT changed hands and the bid is filled.
        invalidateNftOffersQueries(nft.id)
        invalidateBoughtNftQueries(nft)
        trackTransactionCompleted({
          logger,
          flow: Flow.AcceptOffer,
          nft,
          txId,
        })

        sonnerToast.dismiss()
        sonnerToast.custom((id) => (
          <AppToast.Success
            id={id}
            title="Accept offer"
            main={<Trans>Offer accepted</Trans>}
            second={
              <SuccessViewTransaction
                transactionId={txId}
                blockchain={blockchain}
              />
            }
          />
        ))
      }),
    )

    return acceptBidAction
  }

  return { acceptBidActionFactory }
})
